// ─────────────────────────────────────────────────────────────────────────
// DeliveryPhotoModal.tsx
// Bottom-sheet modal wrapping useDeliveryCamera, ported from the
// "8. DELIVERY PHOTO CAPTURE" block + #delivery-photo-modal markup.
// ─────────────────────────────────────────────────────────────────────────

import { useEffect } from 'react';
import { Camera, X, ArrowCounterClockwise, ArrowRight, ArrowsCounterClockwise } from '@phosphor-icons/react';
import { useLanguage } from './LanguageContext';
import { useDeliveryCamera } from './useDeliveryCamera';

export interface DeliveryPhotoModalProps {
  open: boolean;
  onClose: () => void;
  /** Called with the captured/selected photo data URI once the user confirms. */
  onConfirm: (photoDataURI: string) => void;
}

export default function DeliveryPhotoModal({ open, onClose, onConfirm }: DeliveryPhotoModalProps) {
  const { t } = useLanguage();
  const camera = useDeliveryCamera();

  // Stop the camera stream and reset capture state whenever the modal closes,
  // mirroring closeDeliveryPhotoModal() -> stopDeliveryCameraStream().
  useEffect(() => {
    if (!open) camera.retake();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Lock body scroll behind the bottom-sheet while open, matching the
  // original's document.body.classList.add/remove('modal-open').
  useEffect(() => {
    if (!open) return;
    document.body.classList.add('modal-open');
    return () => document.body.classList.remove('modal-open');
  }, [open]);

  if (!open) return null;

  const showVideo = camera.isStreaming;
  const showPreview = Boolean(camera.photoDataURI) && !camera.isStreaming;
  const showPlaceholder = !showVideo && !showPreview;

  // ── Full-screen "real camera" mode while the live stream is active ──
  // Instead of a small boxed preview inside the bottom-sheet, take over the
  // whole viewport (like a phone's native camera app) so the frame is as
  // large as possible for the driver to compose the shot.
  if (showVideo) {
    return (
      <div className="fixed inset-0 z-50 bg-black">
        <video
          ref={camera.videoRef}
          autoPlay
          playsInline
          muted
          onClick={() => camera.capturePhoto()}
          className="h-full w-full object-cover"
          style={camera.facingMode === 'user' ? { transform: 'scaleX(-1)' } : undefined}
        />

        {/* Top bar overlay */}
        <div className="absolute inset-x-0 top-0 flex items-center justify-between bg-gradient-to-b from-black/60 to-transparent px-5 pb-8 pt-[calc(env(safe-area-inset-top)+1rem)]">
          <div>
            <h3 className="text-base font-bold text-white">{t('deliveryPhotoModalTitle')}</h3>
            <p className="text-xs text-white/70">{t('deliveryPhotoModalSubtitle')}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur-sm transition active:bg-white/25"
          >
            <X weight="bold" />
          </button>
        </div>

        {/* Shutter button + camera-switch button */}
        <div className="absolute inset-x-0 bottom-0 flex items-center justify-center gap-6 bg-gradient-to-t from-black/70 to-transparent pb-[calc(env(safe-area-inset-bottom)+2rem)] pt-14">
          <button
            type="button"
            onClick={() => void camera.switchCamera()}
            aria-label="Switch camera"
            className="flex h-12 w-12 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur-sm transition active:scale-95 active:bg-white/25"
          >
            <ArrowsCounterClockwise weight="bold" className="text-xl" />
          </button>
          <button
            type="button"
            onClick={() => camera.capturePhoto()}
            className="flex h-20 w-20 items-center justify-center rounded-full border-4 border-white bg-white/20 shadow-2xl transition active:scale-95"
          >
            <span className="h-16 w-16 rounded-full bg-white" />
          </button>
          {/* Spacer to keep the shutter button visually centered */}
          <div className="h-12 w-12" />
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end bg-brand-900/80 backdrop-blur-sm">
      <div className="mx-auto flex h-[92vh] w-full max-w-md flex-col rounded-t-3xl bg-white shadow-2xl">
        <div className="flex items-center justify-between rounded-t-3xl border-b border-gray-100 bg-brand-50 px-5 py-4">
          <div>
            <h3 className="text-xl font-bold text-brand-900">{t('deliveryPhotoModalTitle')}</h3>
            <p className="text-xs text-gray-500">{t('deliveryPhotoModalSubtitle')}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-100 text-brand-900 transition active:bg-brand-200"
          >
            <X weight="bold" />
          </button>
        </div>

        <div className="flex flex-1 flex-col bg-white p-5">
          <div
            onClick={() => {
              if (showPlaceholder) void camera.openCamera();
            }}
            className="relative flex flex-1 flex-col items-center justify-center gap-3 overflow-hidden rounded-2xl border-2 border-dashed border-brand-300 bg-brand-50 transition hover:border-brand-500"
            style={{ cursor: showPlaceholder ? 'pointer' : 'default' }}
          >
            {showPlaceholder && (
              <>
                <Camera className="text-6xl text-brand-300" />
                <div className="flex flex-col items-center gap-2">
                  <p className="text-base font-semibold text-brand-600">{t('tapToTakePhoto')}</p>
                  <p className="text-xs text-brand-400">{t('photoSizeHint')}</p>
                </div>
              </>
            )}

            {showPreview && (
              <img
                src={camera.photoDataURI ?? ''}
                alt=""
                className="h-full w-full object-contain"
              />
            )}
          </div>

          {camera.error === 'too_large' && (
            <p className="mt-2 text-center text-xs font-semibold text-red-600">{t('photoExceeds10mb')}</p>
          )}

          <input
            ref={camera.fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={camera.handleFileSelected}
          />
        </div>

        <div className="grid grid-cols-2 gap-4 border-t border-gray-100 bg-white p-5 pb-8">
          <button
            type="button"
            onClick={camera.retake}
            className="flex items-center justify-center gap-2 rounded-xl border-2 border-red-200 px-3 py-4 text-sm font-bold text-red-600 transition active:bg-red-50"
          >
            <ArrowCounterClockwise weight="fill" className="text-lg" />
            <span>{t('retake')}</span>
          </button>
          <button
            type="button"
            disabled={!camera.photoDataURI}
            onClick={() => camera.photoDataURI && onConfirm(camera.photoDataURI)}
            className={[
              'flex items-center justify-center gap-2 rounded-xl px-3 py-4 text-sm font-bold shadow-xl transition-all',
              camera.photoDataURI
                ? 'bg-brand-900 text-white active:scale-[0.98] active:bg-brand-800'
                : 'cursor-not-allowed bg-brand-900 text-white opacity-40',
            ].join(' ')}
          >
            <ArrowRight weight="fill" className="text-lg" />
            <span>{t('nextSignature')}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
